import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Subscription.css';

// Razorpay type declaration
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface Plan {
  id: string;
  name: string;
  price: number;
  duration: number;
  description: string;
}

interface CurrentSubscription {
  hasSubscription: boolean;
  canStartTrial: boolean;
  isExpired?: boolean;
  subscription?: {
    id: number;
    plan: string;
    planName: string;
    status: string;
    startDate: string;
    endDate: string;
    daysRemaining: number;
    amount: number;
  };
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [razorpayKey, setRazorpayKey] = useState<string | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    fetchData();
    loadRazorpayScript();
  }, []);

  // Load Razorpay script
  const loadRazorpayScript = () => {
    if (document.getElementById('razorpay-script')) return;

    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, currentRes, keyRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/current'),
        api.get('/payments/key')
      ]);
      setPlans(plansRes.data);
      setCurrentSubscription(currentRes.data);
      setRazorpayKey(keyRes.data.keyId);
      setIsTestMode(keyRes.data.testMode);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setProcessingPlan('free_trial');
      setError('');
      const response = await api.post('/subscriptions/start-trial');
      setSuccess(response.data.message);
      await fetchData();
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start trial');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleSelectPlan = async (planId: string) => {
    try {
      setProcessingPlan(planId);
      setError('');

      // Create order on backend
      const orderResponse = await api.post('/payments/create-order', { plan: planId });
      const orderData = orderResponse.data;

      if (orderData.testMode) {
        // Test mode - show confirmation dialog
        const confirmed = window.confirm(
          `TEST MODE PAYMENT\n\n` +
          `Plan: ${orderData.planName}\n` +
          `Amount: ₹${orderData.amount / 100} (${orderData.amount / 100 / 83} USD approx)\n\n` +
          `Click OK to simulate successful payment.`
        );

        if (confirmed) {
          // Verify test payment
          const verifyResponse = await api.post('/payments/verify', {
            testMode: true,
            plan: planId
          });

          setSuccess(verifyResponse.data.message);
          await fetchData();
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        // Real Razorpay payment
        const options = {
          key: razorpayKey,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'Arc Track',
          description: `${orderData.planName} Subscription`,
          order_id: orderData.orderId,
          handler: async (response: any) => {
            try {
              // Verify payment on backend
              const verifyResponse = await api.post('/payments/verify', {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: planId,
                testMode: false
              });

              setSuccess(verifyResponse.data.message);
              await fetchData();
              setTimeout(() => {
                navigate('/dashboard');
              }, 2000);
            } catch (err: any) {
              setError(err.response?.data?.message || 'Payment verification failed');
            }
          },
          prefill: {
            name: '',
            email: '',
            contact: ''
          },
          theme: {
            color: '#667eea'
          },
          modal: {
            ondismiss: () => {
              setProcessingPlan(null);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate payment');
    } finally {
      if (isTestMode) {
        setProcessingPlan(null);
      }
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      setError('');
      const response = await api.post('/subscriptions/cancel');
      setSuccess(response.data.message);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanBadge = (planId: string) => {
    switch (planId) {
      case 'quarterly':
        return <span className="plan-badge save">Save 33%</span>;
      case 'half_yearly':
        return <span className="plan-badge popular">Popular</span>;
      case 'yearly':
        return <span className="plan-badge best">Best Value</span>;
      default:
        return null;
    }
  };

  // Convert USD to INR (approximate)
  const getINRPrice = (usdPrice: number) => {
    const rate = 83; // Approximate USD to INR rate
    return Math.round(usdPrice * rate);
  };

  if (loading) {
    return (
      <div className="subscription-container">
        <div className="loading">Loading subscription plans...</div>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      <div className="subscription-header">
        <h1>Subscription Plans</h1>
        <p>Choose the plan that works best for you</p>
        {isTestMode && (
          <div style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '10px 20px',
            borderRadius: '8px',
            marginTop: '15px',
            fontSize: '14px'
          }}>
            Test Mode - Payments will be simulated
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Current Subscription Status */}
      {currentSubscription?.hasSubscription && currentSubscription.subscription && (
        <div className="current-subscription">
          <h2>Current Subscription</h2>
          <div className="subscription-details">
            <div className="detail-row">
              <span className="label">Plan:</span>
              <span className="value">{currentSubscription.subscription.planName}</span>
            </div>
            <div className="detail-row">
              <span className="label">Status:</span>
              <span className={`value status-${currentSubscription.subscription.status}`}>
                {currentSubscription.subscription.status.charAt(0).toUpperCase() + currentSubscription.subscription.status.slice(1)}
              </span>
            </div>
            <div className="detail-row">
              <span className="label">Valid Until:</span>
              <span className="value">{formatDate(currentSubscription.subscription.endDate)}</span>
            </div>
            <div className="detail-row">
              <span className="label">Days Remaining:</span>
              <span className={`value ${currentSubscription.subscription.daysRemaining <= 3 ? 'warning' : ''}`}>
                {currentSubscription.subscription.daysRemaining} days
              </span>
            </div>
          </div>
          {currentSubscription.subscription.status === 'active' && (
            <button className="btn-cancel" onClick={handleCancelSubscription}>
              Cancel Subscription
            </button>
          )}
        </div>
      )}

      {/* Expired Subscription Warning */}
      {currentSubscription?.isExpired && (
        <div className="expired-warning">
          <h3>Your subscription has expired!</h3>
          <p>Please renew your subscription to continue using all features.</p>
        </div>
      )}

      {/* Free Trial Card */}
      {currentSubscription?.canStartTrial && (
        <div className="trial-card">
          <div className="trial-icon">🎁</div>
          <h2>Start Your Free Trial</h2>
          <p>Try Arc Track free for 7 days. No credit card required!</p>
          <ul className="trial-features">
            <li>✓ Full access to all features</li>
            <li>✓ Unlimited projects</li>
            <li>✓ Vendor management</li>
            <li>✓ Daily status tracking</li>
          </ul>
          <button
            className="btn-trial"
            onClick={handleStartTrial}
            disabled={processingPlan === 'free_trial'}
          >
            {processingPlan === 'free_trial' ? 'Starting Trial...' : 'Start 7-Day Free Trial'}
          </button>
        </div>
      )}

      {/* Subscription Plans Grid */}
      <div className="plans-grid">
        {plans.filter(plan => plan.id !== 'free_trial').map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${plan.id === 'half_yearly' ? 'featured' : ''}`}
          >
            {getPlanBadge(plan.id)}
            <h3>{plan.name}</h3>
            <div className="plan-price">
              <span className="currency">$</span>
              <span className="amount">{plan.price}</span>
              <span className="period">USD</span>
            </div>
            <div className="plan-price-inr" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              ≈ ₹{getINRPrice(plan.price)} INR
            </div>
            <p className="plan-description">{plan.description}</p>
            <div className="plan-duration">
              <span>{plan.duration} days</span>
            </div>
            <ul className="plan-features">
              <li>✓ Full access to all features</li>
              <li>✓ Unlimited projects</li>
              <li>✓ Vendor management</li>
              <li>✓ Daily status tracking</li>
              <li>✓ Priority support</li>
              {plan.id === 'yearly' && <li>✓ 2 months free</li>}
            </ul>
            <button
              className="btn-subscribe"
              onClick={() => handleSelectPlan(plan.id)}
              disabled={processingPlan === plan.id || (currentSubscription?.hasSubscription && !currentSubscription?.isExpired)}
            >
              {processingPlan === plan.id
                ? 'Processing...'
                : currentSubscription?.hasSubscription && !currentSubscription?.isExpired
                  ? 'Already Subscribed'
                  : `Pay ₹${getINRPrice(plan.price)}`
              }
            </button>
          </div>
        ))}
      </div>

      {/* Payment Info */}
      <div style={{
        textAlign: 'center',
        marginTop: '30px',
        padding: '25px',
        background: 'rgba(255,255,255,0.95)',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        {/* Razorpay Logo */}
        <div style={{ marginBottom: '15px' }}>
          <img
            src="https://razorpay.com/assets/razorpay-logo.svg"
            alt="Razorpay"
            style={{ height: '30px' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.setAttribute('style', 'display: block');
            }}
          />
          <span style={{ display: 'none', fontSize: '20px', fontWeight: 'bold', color: '#072654' }}>Razorpay</span>
        </div>
        <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Secure payment powered by Razorpay. We accept:
        </p>

        {/* Payment Method Logos */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '10px'
        }}>
          {/* Visa */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#1A1F71"/>
              <path d="M21.5 21H19L21 11H23.5L21.5 21Z" fill="white"/>
              <path d="M30.5 11.2C30 11 29.2 10.8 28.2 10.8C25.7 10.8 24 12.1 24 14C24 15.4 25.3 16.2 26.3 16.7C27.3 17.2 27.7 17.5 27.7 18C27.7 18.7 26.8 19 26 19C24.9 19 24.3 18.8 23.4 18.4L23 18.2L22.6 20.8C23.3 21.1 24.5 21.3 25.8 21.3C28.5 21.3 30.1 20 30.2 18C30.2 16.9 29.5 16 28 15.3C27.1 14.8 26.5 14.5 26.5 14C26.5 13.5 27 13 28.1 13C29 13 29.7 13.2 30.2 13.4L30.5 13.5L30.9 11L30.5 11.2Z" fill="white"/>
              <path d="M35.5 11H33.5C32.9 11 32.4 11.2 32.2 11.8L28.5 21H31.2L31.7 19.5H35L35.3 21H37.7L35.5 11ZM32.5 17.5C32.7 17 33.7 14.2 33.7 14.2C33.7 14.2 33.9 13.6 34.1 13.2L34.3 14.1C34.3 14.1 34.9 16.8 35 17.5H32.5Z" fill="white"/>
              <path d="M17.5 11L15 18L14.7 16.5C14.2 15 12.8 13.3 11.2 12.4L13.5 21H16.2L20.2 11H17.5Z" fill="white"/>
              <path d="M13.2 11H9L9 11.2C12.2 12 14.3 14.2 15 16.5L14.2 11.8C14.1 11.2 13.6 11 13.2 11Z" fill="#F9A533"/>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>Visa</span>
          </div>

          {/* Mastercard */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#000"/>
              <circle cx="19" cy="16" r="10" fill="#EB001B"/>
              <circle cx="31" cy="16" r="10" fill="#F79E1B"/>
              <path d="M25 8.5C27.3 10.3 28.8 13 28.8 16C28.8 19 27.3 21.7 25 23.5C22.7 21.7 21.2 19 21.2 16C21.2 13 22.7 10.3 25 8.5Z" fill="#FF5F00"/>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>Mastercard</span>
          </div>

          {/* RuPay */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#097A44"/>
              <text x="8" y="20" fill="white" fontSize="10" fontWeight="bold">RuPay</text>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>RuPay</span>
          </div>

          {/* UPI */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#fff" stroke="#ddd"/>
              <text x="14" y="20" fill="#5F259F" fontSize="12" fontWeight="bold">UPI</text>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>UPI</span>
          </div>

          {/* Google Pay */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#fff" stroke="#ddd"/>
              <text x="6" y="20" fill="#4285F4" fontSize="9" fontWeight="bold">GPay</text>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>Google Pay</span>
          </div>

          {/* PhonePe */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#5F259F"/>
              <text x="4" y="20" fill="white" fontSize="8" fontWeight="bold">PhonePe</text>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>PhonePe</span>
          </div>

          {/* Paytm */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#00B9F5"/>
              <text x="8" y="20" fill="white" fontSize="9" fontWeight="bold">Paytm</text>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>Paytm</span>
          </div>

          {/* Net Banking */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none">
              <rect width="50" height="32" rx="4" fill="#2C3E50"/>
              <path d="M25 6L10 14V18H40V14L25 6Z" fill="#3498DB"/>
              <rect x="12" y="18" width="4" height="8" fill="#ECF0F1"/>
              <rect x="18" y="18" width="4" height="8" fill="#ECF0F1"/>
              <rect x="28" y="18" width="4" height="8" fill="#ECF0F1"/>
              <rect x="34" y="18" width="4" height="8" fill="#ECF0F1"/>
              <rect x="8" y="26" width="34" height="2" fill="#3498DB"/>
            </svg>
            <span style={{ fontSize: '10px', color: '#666' }}>Net Banking</span>
          </div>
        </div>

        {/* Security Badge */}
        <div style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          color: '#28a745',
          fontSize: '13px'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
          </svg>
          <span>100% Secure Payments</span>
        </div>
      </div>

      {/* Back to Dashboard */}
      <div className="back-link">
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default Subscription;
